import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import {
  queryPixelDebug,
  type PixelDebugFilter,
  type PixelDebugRow,
  type PixelDebugQueueRow,
} from "@/lib/pixel-debug.functions";
import { AlertTriangle, Search, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/pixel-debug")({
  component: PixelDebugPage,
  head: () => ({ meta: [{ title: "Pixel Debug — InboxPulse" }] }),
});

const PROVIDERS = ["", "Gmail", "Outlook", "Yahoo", "GMX", "T-Online", "Unknown"];

function PixelDebugPage() {
  const run = useServerFn(queryPixelDebug);
  const [filter, setFilter] = useState<PixelDebugFilter>({
    campaignId: "",
    senderId: "",
    provider: "",
    requestId: "",
    limit: 100,
  });

  const mutation = useMutation({
    mutationFn: (f: PixelDebugFilter) => run({ data: f }),
  });

  const events = mutation.data?.events ?? [];
  const queue = mutation.data?.queue ?? [];
  const err = mutation.error;
  const isAdminError = err instanceof Error && /forbidden|admin/i.test(err.message);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Pixel Debug"
        description="Filter raw tracking events and queue rows by any combination of campaign, sender, provider, or requestId."
      />

      <HelpPanel
        title="Hilfe: Roh-Daten der Öffnungs-Tracker ansehen"
        what={
          <>
            Hier siehst du jede einzelne Anfrage, die unser Tracking-Pixel bekommen hat — wer, wann,
            von welchem Anbieter (Gmail, Apple, …). Wie ein "Logbuch".
          </>
        }
        why={
          <>
            Wenn Statistiken seltsam wirken ("zu viele/zu wenige Öffnungen"), kannst du hier prüfen,
            was wirklich gemessen wurde.
          </>
        }
        how={[
          <>Oben Filter setzen: Kampagne, Sender, Anbieter oder eine Request-ID.</>,
          <>Tabelle zeigt rohen Treffer mit Zeit, IP, User-Agent, Status.</>,
          <>Auf eine Zeile klicken für Details (URL, Header, ggf. Fehlergrund).</>,
        ]}
        tips={[
          <>Test-Mail an dich öffnen → hier muss innerhalb 1-2 Minuten ein Eintrag auftauchen.</>,
          <>Apple-Adresse: 17.x.x.x = das ist Apple's Vorlade-Proxy, nicht der echte Leser.</>,
        ]}
        glossary={[
          {
            term: "User-Agent",
            def: "Verrät, welches Programm das Bild geladen hat (Gmail, Outlook, Apple Mail …).",
          },
          {
            term: "Request-ID",
            def: "Eindeutige Nummer für jede einzelne Pixel-Anfrage. Nützlich beim Fehler suchen.",
          },
          {
            term: "Queue",
            def: "Warteschlange. Treffer werden zuerst gesammelt, dann verarbeitet.",
          },
        ]}
      />

      <Card className="p-4 mb-6">
        <form
          className="grid grid-cols-1 md:grid-cols-5 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(filter);
          }}
        >
          <Field
            label="Campaign ID"
            value={filter.campaignId ?? ""}
            onChange={(v) => setFilter({ ...filter, campaignId: v })}
            placeholder="uuid"
            mono
          />
          <Field
            label="Sender ID"
            value={filter.senderId ?? ""}
            onChange={(v) => setFilter({ ...filter, senderId: v })}
            placeholder="uuid"
            mono
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Provider</label>
            <select
              value={filter.provider ?? ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  provider: e.target.value as PixelDebugFilter["provider"],
                })
              }
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {PROVIDERS.map((p) => (
                <option key={p || "any"} value={p}>
                  {p || "Any"}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Request ID"
            value={filter.requestId ?? ""}
            onChange={(v) => setFilter({ ...filter, requestId: v })}
            placeholder="cf-ray / x-request-id"
            mono
          />
          <div className="flex items-end">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="h-9 w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              <Search className="size-4" />
              {mutation.isPending ? "Searching…" : "Search"}
            </button>
          </div>
        </form>
      </Card>

      {err && (
        <Card className="p-4 mb-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError ? "Admin access required" : "Query failed"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {err instanceof Error ? err.message : String(err)}
            </div>
          </div>
        </Card>
      )}

      {mutation.isSuccess && (
        <>
          <Section title={`Events (${events.length})`}>
            {events.length === 0 ? (
              <Empty>No tracking_events match the filter.</Empty>
            ) : (
              <EventsTable rows={events} />
            )}
          </Section>

          <Section
            title={`Queue (${queue.length})`}
            subtitle="In-flight + failed rows still in tracking_event_queue"
          >
            {queue.length === 0 ? (
              <Empty>No queue rows match the filter.</Empty>
            ) : (
              <QueueTable rows={queue} />
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "h-9 rounded-md border border-input bg-background px-2 text-sm",
          mono && "font-mono text-xs",
        )}
      />
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-8">
      <div className="mb-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <Card className="p-0 overflow-hidden">{children}</Card>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-8 text-center text-sm text-muted-foreground">{children}</div>;
}

function CopyableId({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      onClick={() => navigator.clipboard.writeText(value)}
      title={value}
      className="font-mono text-xs hover:underline inline-flex items-center gap-1 max-w-[180px] truncate"
    >
      <span className="truncate">{value}</span>
      <Copy className="size-3 opacity-50 shrink-0" />
    </button>
  );
}

function EventsTable({ rows }: { rows: PixelDebugRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-muted-foreground text-xs">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Occurred</th>
            <th className="text-left px-3 py-2 font-medium">Event</th>
            <th className="text-left px-3 py-2 font-medium">Campaign</th>
            <th className="text-left px-3 py-2 font-medium">Sender</th>
            <th className="text-left px-3 py-2 font-medium">Provider</th>
            <th className="text-left px-3 py-2 font-medium">Device</th>
            <th className="text-left px-3 py-2 font-medium">Country</th>
            <th className="text-left px-3 py-2 font-medium">Request ID</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/50">
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {new Date(r.occurred_at).toLocaleString()}
              </td>
              <td className="px-3 py-2 text-xs">{r.event_type}</td>
              <td className="px-3 py-2">
                <CopyableId value={r.campaign_id} />
              </td>
              <td className="px-3 py-2">
                <CopyableId value={r.sender_id} />
              </td>
              <td className="px-3 py-2 text-xs">{r.provider ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{r.device ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{r.country ?? "—"}</td>
              <td className="px-3 py-2">
                <CopyableId value={r.request_id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueueTable({ rows }: { rows: PixelDebugQueueRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-muted-foreground text-xs">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Enqueued</th>
            <th className="text-left px-3 py-2 font-medium">Status</th>
            <th className="text-left px-3 py-2 font-medium">Attempts</th>
            <th className="text-left px-3 py-2 font-medium">Event</th>
            <th className="text-left px-3 py-2 font-medium">Campaign</th>
            <th className="text-left px-3 py-2 font-medium">Sender</th>
            <th className="text-left px-3 py-2 font-medium">Provider</th>
            <th className="text-left px-3 py-2 font-medium">Request ID</th>
            <th className="text-left px-3 py-2 font-medium">Last error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/50">
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {new Date(r.enqueued_at).toLocaleString()}
              </td>
              <td className="px-3 py-2 text-xs">
                <span
                  className={cn(
                    "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium",
                    r.status === "failed" && "bg-destructive/10 text-destructive",
                    r.status === "processing" && "bg-amber-500/10 text-amber-600",
                    r.status === "pending" && "bg-muted text-muted-foreground",
                  )}
                >
                  {r.status}
                </span>
              </td>
              <td className="px-3 py-2 text-xs tabular-nums">{r.attempts}</td>
              <td className="px-3 py-2 text-xs">{r.event_type}</td>
              <td className="px-3 py-2">
                <CopyableId value={r.campaign_id} />
              </td>
              <td className="px-3 py-2">
                <CopyableId value={r.sender_id} />
              </td>
              <td className="px-3 py-2 text-xs">{r.provider ?? "—"}</td>
              <td className="px-3 py-2">
                <CopyableId value={r.request_id} />
              </td>
              <td
                className="px-3 py-2 text-xs text-destructive max-w-[260px] truncate"
                title={r.last_error ?? undefined}
              >
                {r.last_error ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
